import React, { useEffect, useState, useRef } from 'react';
import Slider from 'react-slick';
import './Slider.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ProductInfoCard from '../ProductInfoCard/ProductInfoCard';

// `staticProducts` switches the carousel to a hand-picked list: it renders
// exactly those products, in that order, and skips the category lookups
// entirely. That is what the "picked products" block of the Home layout uses.
function ProductCarousel({ category, products: initialProducts = [], displayName, accessibleProductIds, isFeatured = false, staticProducts = null }) {
  const [products, setProducts] = useState(initialProducts);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [categoryDisplayName, setCategoryDisplayName] = useState(displayName || '');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isDraggingRef = useRef(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const videoRefs = useRef({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const handleMouseEnter = (uniqueKey) => {
    if (isTouchDevice()) return;
    setHoveredProductId(uniqueKey);
  };

  const handleMouseLeave = (uniqueKey) => {
    if (isTouchDevice()) return;
    setHoveredProductId(null);
  };

  useEffect(() => {
    const fetchCategoryDetails = async () => {
      try {
        const response = await fetch(`${backendUrl}/category/details/${category}`);
        const data = await response.json();
        if (data.success) {
          setCategoryDisplayName(data.category.displayName);
        } else {
          console.error('Error fetching category details:', data.message);
        }
      } catch (err) {
        console.error('Error fetching category details:', err);
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await fetch(`${backendUrl}/product/category/${category}`);
        const data = await response.json();
        if (data.success) {
          setProducts(data.products);
        } else {
          console.error('Error fetching products:', data.message);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };

    if (staticProducts) {
      setProducts(staticProducts);
      return;
    }

    // Always fetch to ensure event data is populated
    fetchProducts();
    if (!displayName) {
      fetchCategoryDetails();
    }
  }, [category, backendUrl, displayName, staticProducts]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSlidesToShow = () => {
    if (isFeatured) {
      if (windowWidth >= 1400) return 4;
      if (windowWidth >= 1200) return 3;
      if (windowWidth >= 992) return 3;
      if (windowWidth >= 768) return 2;
      if (windowWidth >= 576) return 2;
      return 1;
    }
    if (windowWidth >= 1400) return 7;
    if (windowWidth >= 1200) return 6;
    if (windowWidth >= 992) return 5;
    if (windowWidth >= 768) return 4;
    if (windowWidth >= 576) return 3;
    return 2;
  };

  useEffect(() => {
    const slidesToShow = getSlidesToShow();
    let temp = [...products];
    if (products.length > 0 && products.length < slidesToShow) {
      while (temp.length < slidesToShow) {
        temp = temp.concat(products);
      }
      temp = temp.slice(0, slidesToShow);
    }
    setDisplayProducts(temp);
  }, [products, windowWidth]);

  const handleProductClick = (product) => {
    if (isDraggingRef.current) return;
    setSelectedProduct(product);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  if (products.length === 0) return null;

  const normalizeProjection = (p) => {
    const upper = (p || '').toUpperCase();
    if (upper === 'NONE') return '2D';
    if (upper === '360' || upper === '360_TB') return '360';
    if (upper === '180' || upper === '180_NONO') return '180';
    return '2D';
  };

  const settings = {
    key: windowWidth,
    infinite: true,
    speed: 400,
    slidesToShow: getSlidesToShow(),
    slidesToScroll: getSlidesToShow(),
    autoplay: true,
    autoplaySpeed: 10000,
    arrows: true,
    nextArrow: (
      <div className="slick-next">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </div>
    ),
    prevArrow: (
      <div className="slick-prev">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </div>
    ),
    beforeChange: () => { isDraggingRef.current = true; },
    afterChange: () => { setTimeout(() => { isDraggingRef.current = false; }, 100); },
  };

  return (
    <div className={`product-carousel-container${isFeatured ? ' featured-carousel' : ''}`}>
      <h2 className="slider-title">{categoryDisplayName}</h2>
      <Slider {...settings}>
        {displayProducts.map((product, index) => {
          const uniqueKey = `${product._id}-${index}`;
          const isHovered = hoveredProductId === uniqueKey;
          const hasVideo = !!product.featuredVideo;
          const event = product.event;
          const projections = event?.video_types || [];
          const uniqueProjections = [...new Set(projections.map(normalizeProjection))].filter(Boolean);
          const hasAccess = accessibleProductIds !== null && accessibleProductIds !== undefined && accessibleProductIds.has(product._id);

          return (
            <div
              key={uniqueKey}
              className="carousel-slide"
              onClick={() => handleProductClick(product)}
              onMouseEnter={() => handleMouseEnter(uniqueKey)}
              onMouseLeave={() => handleMouseLeave(uniqueKey)}
            >
              {hasAccess && (
                <div className="card-access-badge">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="white" stroke="none">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                </div>
              )}
              <div className="card-thumbnail">
                <img
                  src={product.featuredImage}
                  alt={product.title}
                  className="product-image base-image"
                />
                {!isTouchDevice() && hasVideo && isHovered && (
                  <video
                    ref={(el) => {
                      videoRefs.current[uniqueKey] = el;
                      if (el) {
                        el.play().catch(() => {});
                      }
                    }}
                    src={product.featuredVideo}
                    muted
                    loop
                    playsInline
                    autoPlay
                    preload="auto"
                    className="product-image hover-video"
                  />
                )}
                {uniqueProjections.length > 0 && (
                  <div className="card-badges">
                    {uniqueProjections.map((proj) => (
                      <span key={proj} className="card-badge">{proj}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </Slider>

      <ProductInfoCard
        product={selectedProduct}
        handleClosePopup={handleClosePopup}
        showPopup={showPopup}
      />
    </div>
  );
}

export default React.memo(ProductCarousel);
